// Renders a saved-tip card into a 1080×1350 PNG (Instagram-Story-shaped)
// and triggers the native share sheet, or falls back to downloading.
//
// No external deps — pure Canvas + Web Share API. Works in iOS Capacitor
// and modern browsers. Image assets must be same-origin for export to
// succeed (our landmark JPGs live in /assets/landmarks/).

import { formatMoney } from "./currency";
import { getCountry } from "./tipping";
import QRCode from "qrcode";
import { drawSticker, drawFrame } from "./shareStickers";

const W = 1080;
const H = 1620;

/**
 * URL the QR code resolves to. Each share-PNG carries a tiny QR code in the
 * footer so anyone seeing the screenshot can install the app in 1 scan.
 * Update via tiplingo.app's app-store-redirect page (currently the marketing
 * site auto-detects iOS/Android and forwards to the right store).
 */
const QR_URL = "https://tiplingo.app";

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function drawText(ctx, text, opts) {
  const {
    x,
    y,
    color = "#0E2C54",
    align = "left",
    baseline = "alphabetic",
    weight = "800",
    size = 48,
    family = "'Plus Jakarta Sans', system-ui, sans-serif",
    letterSpacing = 0,
    maxWidth = null,
  } = opts;
  ctx.font = `${weight} ${size}px ${family}`;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = baseline;
  // letterSpacing isn't supported by all canvas impls; fall back to fillText.
  if (letterSpacing && ctx.letterSpacing !== undefined) {
    ctx.letterSpacing = `${letterSpacing}px`;
  }
  if (maxWidth) {
    ctx.fillText(text, x, y, maxWidth);
  } else {
    ctx.fillText(text, x, y);
  }
}

function wrapLines(ctx, text, maxWidth, fontSpec) {
  ctx.font = fontSpec;
  const words = String(text || "").split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const trial = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(trial).width > maxWidth && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = trial;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/**
 * Render a saved-tip entry to a 1080×1350 canvas and return a Blob.
 * @param {object} entry the tip object
 * @param {object} [opts]
 * @param {string} [opts.sticker] optional sticker id from `shareStickers.STICKERS`
 */
export async function renderTipCardBlob(entry, opts = {}) {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Bone background
  ctx.fillStyle = "#F8F6F0";
  ctx.fillRect(0, 0, W, H);

  // Navy hero band at top
  const HERO_H = 240;
  ctx.fillStyle = "#0E2C54";
  ctx.fillRect(0, 0, W, HERO_H);

  // Landmark — top-right corner of the navy band, masked into a soft
  // rounded rectangle so it reads as a stamp/decoration rather than a
  // full-bleed watermark.
  const cc = entry.country_code;
  if (cc) {
    try {
      const lm = await loadImage(`/assets/landmarks/${cc}.jpg`);
      const LW = 360;
      const LH = 200;
      const LX = W - LW - 40;
      const LY = (HERO_H - LH) / 2;
      ctx.save();
      roundRect(ctx, LX, LY, LW, LH, 20);
      ctx.clip();
      // object-cover scaling inside the rounded rect
      const ratio = Math.max(LW / lm.width, LH / lm.height);
      const dw = lm.width * ratio;
      const dh = lm.height * ratio;
      const dx = LX + (LW - dw) / 2;
      const dy = LY + (LH - dh) / 2;
      ctx.globalAlpha = 0.95;
      ctx.drawImage(lm, dx, dy, dw, dh);
      ctx.restore();
      // subtle border
      ctx.save();
      ctx.strokeStyle = "rgba(255,255,255,0.18)";
      ctx.lineWidth = 2;
      roundRect(ctx, LX, LY, LW, LH, 20);
      ctx.stroke();
      ctx.restore();
    } catch (e) {
      console.error("shareTipCard: badge/asset draw failed", e);
    }
  }

  // Brand wordmark — "Tip" stays white on navy, "Lingo" pops in orange to
  // match the in-app branding.
  ctx.font = `900 92px 'Plus Jakarta Sans', system-ui, sans-serif`;
  const tipW = ctx.measureText("Tip").width;
  drawText(ctx, "Tip", {
    x: 60,
    y: 150,
    color: "#FFFFFF",
    size: 92,
    weight: "900",
    letterSpacing: -1,
  });
  drawText(ctx, "Lingo", {
    x: 60 + tipW,
    y: 150,
    color: "#E55B2C",
    size: 92,
    weight: "900",
    letterSpacing: -1,
  });

  // Tagline
  drawText(ctx, "TRAVEL REPORT", {
    x: 60,
    y: 200,
    color: "#5DD16F",
    size: 26,
    weight: "800",
    letterSpacing: 3,
  });

  // Card container (rounded white "receipt")
  const CARD_X = 60;
  const CARD_Y = 290;
  const CARD_W = W - 120;
  const CARD_H = H - CARD_Y - 130;
  ctx.save();
  roundRect(ctx, CARD_X, CARD_Y, CARD_W, CARD_H, 32);
  ctx.fillStyle = "#FFFFFF";
  ctx.fill();
  ctx.strokeStyle = "rgba(14,44,84,0.08)";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();

  // QR code — top-right of the white card in default mode. In postcard mode
  // we defer drawing until after the photo+note block so it can sit at
  // bottom-right next to the message (matches user's mockup).
  const QR_PX = 170;
  const QR_X = CARD_X + CARD_W - 50 - QR_PX;
  const QR_Y = CARD_Y + 110;
  const postcardMode = !!opts.hideAmounts;
  let qrCanvas = null;
  try {
    qrCanvas = document.createElement("canvas");
    await QRCode.toCanvas(qrCanvas, QR_URL, {
      width: QR_PX,
      margin: 2,
      color: { dark: "#0E2C54", light: "#FFFFFF" },
      errorCorrectionLevel: "M",
    });
    if (!postcardMode) {
      ctx.save();
      ctx.drawImage(qrCanvas, QR_X, QR_Y, QR_PX, QR_PX);
      ctx.restore();
      drawText(ctx, "tiplingo.app", {
        x: QR_X + QR_PX / 2,
        y: QR_Y + QR_PX + 28,
        color: "#4A5D78",
        size: 20,
        weight: "700",
        align: "center",
        letterSpacing: 1,
      });
    }
  } catch {
    /* QR generation should never fail at this size; if it does we fall
       back to a plain text URL below in the footer. */
    qrCanvas = null;
  }

  // Country flag + name + currency. Hidden in "postcard mode" (when amounts
  // are masked) since the country pill sits in the same row as the upcoming
  // numbers we're suppressing, and removing it lets the photo+note stretch
  // up the card for a cleaner, brand-forward postcard.
  const country = cc ? getCountry(cc) : null;
  const countryLabel = country?.name || cc || "Trip";
  if (!opts.hideAmounts) {
    drawText(ctx, `${country?.flag || "🌍"}  ${countryLabel.toUpperCase()}  ·  ${entry.currency || "USD"}`, {
      x: CARD_X + 50,
      y: CARD_Y + 75,
      color: "#4A5D78",
      size: 28,
      weight: "800",
      letterSpacing: 2,
    });
  }

  // Stats block — TOTAL / BILL / TIP / PARTY plus dividers. Entirely skipped
  // in postcard mode (`opts.hideAmounts`), reclaiming ~470px of vertical
  // space for a full-width hero photo and a clean message block below it.
  if (!postcardMode) {
    const totalText = formatMoney(entry.total ?? 0, entry.currency || "USD");
    const totalMaxW = QR_X - CARD_X - 50 - 30; // 30px gutter between total and QR
    ctx.font = `900 104px 'Plus Jakarta Sans', system-ui, sans-serif`;
    let totalSize = 104;
    while (ctx.measureText(totalText).width > totalMaxW && totalSize > 60) {
      totalSize -= 6;
      ctx.font = `900 ${totalSize}px 'Plus Jakarta Sans', system-ui, sans-serif`;
    }
    drawText(ctx, "TOTAL PAID", {
      x: CARD_X + 50,
      y: CARD_Y + 130,
      color: "#4A5D78",
      size: 22,
      weight: "800",
      letterSpacing: 3,
    });
    drawText(ctx, totalText, {
      x: CARD_X + 50,
      y: CARD_Y + 220,
      color: "#0E2C54",
      size: totalSize,
      weight: "900",
    });

    // Divider — sits below both the total amount and the QR block
    ctx.fillStyle = "rgba(14,44,84,0.10)";
    ctx.fillRect(CARD_X + 50, CARD_Y + 320, CARD_W - 100, 2);

    // Bill + tip% + people row
    const bill = formatMoney(entry.bill ?? 0, entry.currency || "USD");
    const tipPct =
      typeof entry.tip_pct === "number" && entry.tip_pct >= 0
        ? `${Math.round(entry.tip_pct)}%`
        : "—";
    const tipAmt =
      typeof entry.tip_amount === "number"
        ? formatMoney(entry.tip_amount, entry.currency || "USD")
        : null;
    const people = entry.people ? `${entry.people} ppl` : null;

    const statY = CARD_Y + 360;

    // Vertical dividers between BILL | TIP | PARTY columns (matches mockup)
    ctx.fillStyle = "rgba(14,44,84,0.10)";
    const colDivTop = statY - 30;
    const colDivH = 110;
    ctx.fillRect(CARD_X + CARD_W / 3, colDivTop, 2, colDivH);
    ctx.fillRect(CARD_X + (2 * CARD_W) / 3, colDivTop, 2, colDivH);

    drawText(ctx, "BILL", {
      x: CARD_X + 50,
      y: statY - 8,
      color: "#4A5D78",
      size: 18,
      weight: "800",
      letterSpacing: 2,
    });
    drawText(ctx, bill, {
      x: CARD_X + 50,
      y: statY + 40,
      color: "#0E2C54",
      size: 44,
      weight: "800",
    });

    drawText(ctx, "TIP", {
      x: CARD_X + CARD_W / 2,
      y: statY - 8,
      color: "#4A5D78",
      size: 18,
      weight: "800",
      align: "center",
      letterSpacing: 2,
    });
    drawText(ctx, tipPct, {
      x: CARD_X + CARD_W / 2,
      y: statY + 40,
      color: "#E55B2C",
      size: 44,
      weight: "800",
      align: "center",
    });
    if (tipAmt) {
      drawText(ctx, tipAmt, {
        x: CARD_X + CARD_W / 2,
        y: statY + 72,
        color: "#4A5D78",
        size: 22,
        weight: "700",
        align: "center",
      });
    }

    if (people) {
      drawText(ctx, "PARTY", {
        x: CARD_X + CARD_W - 50,
        y: statY - 8,
        color: "#4A5D78",
        size: 18,
        weight: "800",
        align: "right",
        letterSpacing: 2,
      });
      drawText(ctx, people, {
        x: CARD_X + CARD_W - 50,
        y: statY + 40,
        color: "#0E2C54",
        size: 44,
        weight: "800",
        align: "right",
      });
    }

    // Divider between stats row and photo+note section
    ctx.fillStyle = "rgba(14,44,84,0.10)";
    ctx.fillRect(CARD_X + 50, CARD_Y + 470, CARD_W - 100, 2);
  }

  // Photo + note section — two layouts:
  //   - Default: photo on LEFT (~430w x 510h), note on RIGHT, anchored
  //     below the stats block.
  //   - Postcard mode (`opts.hideAmounts`): full-width hero photo at the
  //     top of the card, with the title/body/stars stacked beneath it in a
  //     single full-width column. QR moves to bottom-right next to the
  //     message so the card reads as a clean travel postcard.
  const SECTION_Y = postcardMode ? CARD_Y + 50 : CARD_Y + 500;
  // In postcard mode we let the photo box adapt to the natural aspect
  // ratio of the photo so nothing is cropped (capped to MAX_PHOTO_H so
  // the note + QR still fit underneath). In default mode the box stays
  // fixed and the photo is object-cover cropped to fill.
  // Photo box adapts to the source photo's natural aspect ratio so
  // WYSIWYG matches the Adjust Frame modal (no head-cropping). Box is
  // capped at POSTCARD_MAX_W × POSTCARD_MAX_H — we use the FULL card
  // width for landscape photos and grow as tall as possible (capped to
  // leave room for the note column + QR + date underneath) for
  // portraits.
  const POSTCARD_MAX_W = CARD_W - 40;        // 20px margin each side
  const POSTCARD_MAX_H = 870;                // leaves ≥14px gap above QR top (1234)
  let PHOTO_BOX_W = postcardMode ? POSTCARD_MAX_W : 430;
  let PHOTO_BOX_H = postcardMode ? POSTCARD_MAX_H : 510;
  let noteX = CARD_X + 50;
  let noteY = SECTION_Y;
  let noteMaxW = CARD_W - 100;
  let stickerAnchor = null;
  let photoBounds = null; // captured for frame overlay
  // In postcard mode we reserve bottom-right space for the QR; the note
  // column needs to wrap before colliding with it.
  const QR_RESERVE_W = postcardMode ? QR_PX + 50 : 0;

  if (entry.food_photo_base64) {
    try {
      const src = entry.food_photo_base64.startsWith("data:")
        ? entry.food_photo_base64
        : `data:image/jpeg;base64,${entry.food_photo_base64}`;
      const photo = await loadImage(src);

      // Adapt the photo box to the photo's natural aspect ratio in
      // postcard mode so the entire image is visible (no cropping) and
      // the share output matches the Adjust Frame modal preview.
      // Landscape → full card width, height follows aspect.
      // Portrait → height capped at POSTCARD_MAX_H, width follows
      //   aspect (centered horizontally in the card).
      if (postcardMode) {
        const aspect = photo.width / photo.height; // >1 = landscape
        if (aspect >= POSTCARD_MAX_W / POSTCARD_MAX_H) {
          PHOTO_BOX_W = POSTCARD_MAX_W;
          PHOTO_BOX_H = Math.round(PHOTO_BOX_W / aspect);
        } else {
          PHOTO_BOX_H = POSTCARD_MAX_H;
          PHOTO_BOX_W = Math.round(PHOTO_BOX_H * aspect);
        }
      }

      // Horizontal centering — full-width in default mode, centered in
      // postcard mode when the photo is portrait.
      const PX = postcardMode
        ? CARD_X + Math.round((CARD_W - PHOTO_BOX_W) / 2)
        : CARD_X + 50;
      const PY = SECTION_Y;

      const photoX = PX;
      const photoY = PY;
      const photoW = PHOTO_BOX_W;
      const photoH = PHOTO_BOX_H;

      // In postcard mode the box matches the photo aspect exactly, so a
      // "fit" draw shows the entire photo with no cropping (matches the
      // Adjust Frame preview). In default mode we object-cover crop.
      let dw;
      let dh;
      let dx;
      let dy;
      if (postcardMode) {
        dw = photoW;
        dh = photoH;
        dx = photoX;
        dy = photoY;
      } else {
        const ratio = Math.max(photoW / photo.width, photoH / photo.height);
        dw = photo.width * ratio;
        dh = photo.height * ratio;
        dx = photoX + (photoW - dw) / 2;
        dy = photoY + (photoH - dh) / 2;
      }

      ctx.save();
      roundRect(ctx, photoX, photoY, photoW, photoH, 24);
      ctx.clip();
      ctx.drawImage(photo, dx, dy, dw, dh);
      ctx.restore();
      // Border stroke only when there's no frame (the frame replaces it).
      if (!opts.frame) {
        ctx.save();
        ctx.strokeStyle = "rgba(14,44,84,0.10)";
        ctx.lineWidth = 2;
        roundRect(ctx, photoX, photoY, photoW, photoH, 24);
        ctx.stroke();
        ctx.restore();
      }

      // Bounds shared by the frame overlay AND the sticker positioning.
      photoBounds = { x: PX, y: PY, w: PHOTO_BOX_W, h: PHOTO_BOX_H };

      if (postcardMode) {
        // Note column anchors BELOW the photo, spanning the full card.
        noteX = CARD_X + 50;
        noteY = PY + PHOTO_BOX_H + 50;
        noteMaxW = CARD_W - 100 - QR_RESERVE_W;
      } else {
        // Note column anchors to the RIGHT of the photo.
        noteX = PX + PHOTO_BOX_W + 40;
        noteY = SECTION_Y + 20;
        noteMaxW = CARD_X + CARD_W - noteX - 50;
      }

      // Sticker anchor. If the user dragged the sticker to a custom
      // position (opts.stickerPos is a {x,y} pair normalized 0-1 to the
      // VISIBLE photo area as shown in the modal preview), map that back
      // to the actual photo bounds on the canvas (which may be smaller
      // than the frame outer when a frame is applied). Otherwise fall
      // back to the default top-right "stamp" placement on the photo.
      if (opts.stickerPos) {
        stickerAnchor = {
          x: photoX + opts.stickerPos.x * photoW,
          y: photoY + opts.stickerPos.y * photoH,
        };
      } else {
        stickerAnchor = postcardMode
          ? { x: photoX + photoW - 150, y: photoY + 50 }
          : { x: photoX + photoW * 0.42, y: photoY + 35 };
      }
    } catch (e) {
      console.error("shareTipCard: photo decode failed — falling back to full-width note", e);
    }
  }
  if (!stickerAnchor) {
    stickerAnchor = { x: CARD_X + CARD_W - 80, y: SECTION_Y - 20 };
  }

  // Note — bold title (e.g. restaurant) first, then the body. The right
  // column is taller than before so multi-line reviews now fit comfortably.
  if (entry.note && String(entry.note).trim()) {
    const raw = String(entry.note).trim();
    const titleMatch = raw.match(/^(.{1,80}?)([\n·•—\-]\s*)(.+)$/s);
    const title = titleMatch ? titleMatch[1].trim() : null;
    const body = titleMatch ? titleMatch[3].trim() : raw;

    let ny = noteY;
    const hasPhoto = !!entry.food_photo_base64;
    // Postcard mode uses the full card width for the note, so we bump the
    // font sizes back up to the "no photo" scale for impact.
    const wideNote = postcardMode || !hasPhoto;

    if (title) {
      const titleSize = wideNote ? 46 : 34;
      const titleLineH = wideNote ? 56 : 42;
      const titleFont = `800 ${titleSize}px 'Plus Jakarta Sans', system-ui, sans-serif`;
      const titleLines = wrapLines(ctx, title, noteMaxW, titleFont).slice(0, 2);
      for (const line of titleLines) {
        drawText(ctx, line, {
          x: noteX,
          y: ny,
          color: "#0E2C54",
          size: titleSize,
          weight: "800",
        });
        ny += titleLineH;
      }
      ny += 16;
    }

    // Body font sized to the column width so paired layout still reads well.
    const bodySize = wideNote ? 32 : 28;
    const bodyLineH = wideNote ? 44 : 38;
    const bodyFont = `600 ${bodySize}px 'Plus Jakarta Sans', system-ui, sans-serif`;

    // Render each paragraph (\n-separated) independently so authored
    // line-breaks survive into the share image. Within each paragraph we
    // word-wrap to the note column width.
    const paragraphs = body.split(/\n+/).map((s) => s.trim()).filter(Boolean);
    let lineCount = 0;
    const maxLines = postcardMode ? 6 : hasPhoto ? 12 : 10;
    outer: for (const para of paragraphs) {
      const lines = wrapLines(ctx, para, noteMaxW, bodyFont);
      for (const line of lines) {
        if (lineCount >= maxLines) break outer;
        drawText(ctx, line, {
          x: noteX,
          y: ny,
          color: "#243140",
          size: bodySize,
          weight: "600",
        });
        ny += bodyLineH;
        lineCount++;
        // If the line names a rating (e.g. "5 stars"), render a yellow
        // star row immediately beneath it. Matches the mockup's review.
        const starMatch = line.match(/(\d)\s*stars?/i);
        if (starMatch) {
          const numStars = Math.max(1, Math.min(5, parseInt(starMatch[1], 10)));
          const starSize = 40;
          const starGap = 8;
          ctx.font = `900 ${starSize}px 'Plus Jakarta Sans', system-ui, sans-serif`;
          ctx.fillStyle = "#F4B400";
          ctx.textAlign = "left";
          ctx.textBaseline = "alphabetic";
          let sx = noteX;
          for (let i = 0; i < numStars; i++) {
            ctx.fillText("★", sx, ny + 8);
            sx += starSize + starGap;
          }
          ny += starSize + 12;
        }
      }
      // Paragraph break — small extra gap
      if (lineCount < maxLines) {
        ny += 6;
      }
    }
  }

  // QR (postcard mode) — bottom-right corner of the card, next to the
  // message block. Default mode already drew the QR at top-right.
  if (postcardMode && qrCanvas) {
    const pmQrX = CARD_X + CARD_W - 50 - QR_PX;
    const pmQrY = CARD_Y + CARD_H - 50 - QR_PX - 36;
    ctx.save();
    ctx.drawImage(qrCanvas, pmQrX, pmQrY, QR_PX, QR_PX);
    ctx.restore();
    drawText(ctx, "tiplingo.app", {
      x: pmQrX + QR_PX / 2,
      y: pmQrY + QR_PX + 28,
      color: "#4A5D78",
      size: 20,
      weight: "700",
      align: "center",
      letterSpacing: 1,
    });
  }

  // Date pill at very bottom of card
  if (entry.created_at) {
    try {
      const dStr = new Date(entry.created_at).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
      drawText(ctx, dStr.toUpperCase(), {
        x: CARD_X + CARD_W / 2,
        y: CARD_Y + CARD_H - 30,
        color: "#4A5D78",
        size: 20,
        weight: "800",
        align: "center",
        letterSpacing: 3,
      });
    } catch (e) {
      console.error("shareTipCard: date formatting failed", e);
    }
  }

  // Frame overlay — drawn AFTER the photo so it wraps the edges, and
  // BEFORE the sticker so the sticker sits on top of the frame. The
  // user's pinch/drag adjustments arrive as `opts.frameTransform`:
  //   scale=1.15 means the frame is rendered 15% larger than the photo
  //   box (centered on the photo) so the decorative border sits OUTSIDE
  //   the picture and doesn't clip it. offset shifts the frame relative
  //   to the photo size, normalized -0.4..0.4.
  if (opts.frame && photoBounds) {
    try {
      const ft = opts.frameTransform || { scale: 1, offsetX: 0, offsetY: 0 };
      const fw = photoBounds.w * ft.scale;
      const fh = photoBounds.h * ft.scale;
      const fx =
        photoBounds.x +
        (photoBounds.w - fw) / 2 +
        (ft.offsetX || 0) * photoBounds.w;
      const fy =
        photoBounds.y +
        (photoBounds.h - fh) / 2 +
        (ft.offsetY || 0) * photoBounds.h;
      await drawFrame(ctx, opts.frame, { x: fx, y: fy, w: fw, h: fh });
    } catch (e) {
      console.error("shareTipCard: frame asset draw failed", e);
    }
  }

  // Sticker — drawn LAST so it sits on top of the photo, frame & note as
  // a "stamp". `drawSticker` is async for image-based v2 stickers.
  if (opts.sticker && stickerAnchor) {
    try {
      const stickerScale = opts.stickerPos?.scale ?? 1;
      await drawSticker(
        ctx,
        opts.sticker,
        stickerAnchor.x,
        stickerAnchor.y,
        stickerScale
      );
    } catch {
      /* sticker draw failed — fail silent so the share still ships */
    }
  }

  // Footer — left side: brand tag; right side: QR code
  drawText(ctx, "MADE WITH TIPLINGO", {
    x: 60,
    y: H - 60,
    color: "#0E2C54",
    size: 22,
    weight: "800",
    letterSpacing: 4,
  });
  drawText(ctx, "Scan to install", {
    x: 60,
    y: H - 30,
    color: "#4A5D78",
    size: 18,
    weight: "600",
    letterSpacing: 1,
  });

  // QR is drawn at the top-right of the card (see above). Footer is text-only.

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
      "image/png",
      0.95
    );
  });
}

export async function shareTipCard(entry, opts = {}) {
  const blob = await renderTipCardBlob(entry, opts);
  const filename = `tiplingo-${entry.country_code || "trip"}-${
    new Date(entry.created_at || Date.now()).toISOString().slice(0, 10)
  }.png`;
  const file = new File([blob], filename, { type: "image/png" });

  // Prefer the native share sheet on iOS / Android.
  //
  // Notes on iOS Safari behaviour:
  //   - iOS 15+ supports `navigator.share({files})` with PNG/JPEG.
  //   - `navigator.canShare()` sometimes returns `false` on iOS even when
  //     `navigator.share` would actually succeed. We therefore TRY `share()`
  //     first and only fall back to a download if it throws — except when
  //     the user explicitly cancels (AbortError / NotAllowedError /
  //     ShareTimeoutError → silently exit, no download).
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      // Intent-aware copy: when triggered from the "Post to Instagram
      // Stories" CTA we surface a caption that nudges the user toward
      // IG (it shows up as the share-sheet preview text on iOS).
      const isInstagram = opts.intent === "instagram";
      await navigator.share({
        files: [file],
        title: isInstagram ? "Share to Instagram Stories" : "My TipLingo travel tip",
        text: isInstagram
          ? `My travel postcard from ${entry.country_code || "—"} — made with TipLingo ✈️`
          : `Settled up in ${entry.country_code || "—"} with TipLingo`,
      });
      return { method: "native-share" };
    } catch (e) {
      const name = e?.name || "";
      if (
        name === "AbortError" ||
        name === "NotAllowedError" ||
        name === "ShareTimeoutError"
      ) {
        // User cancelled the share sheet — don't surprise them with a
        // download. Exit cleanly.
        return { method: "cancelled" };
      }
      // Other errors (DataError, SecurityError, etc.) → fall through to
      // the download fallback so the user still walks away with the PNG.
    }
  }

  // Fallback: trigger a download
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { method: "download" };
}
